import { ComponentType } from 'react';

import { token } from '@atlaskit/tokens';

import { BitkitIconTypes, IconSizes } from './types';

/**
 * Internal Icon wrapper used by our custom Bitkit icons.
 *
 * Drop-in replacement for the legacy `<Icon glyph={...}>` API from
 * `@atlaskit/icon`, which was removed in the major bump landed in
 * COREX-13424. The new `@atlaskit/icon` only supports Atlaskit's
 * "core" / "utility" glyph sets and does not accept arbitrary SVG glyphs.
 *
 * Matches the old `Icon` behaviour:
 * - The outer wrapper controls sizing via explicit width/height.
 * - The glyph receives only `role="presentation"` (no width/height props),
 *   so existing glyphs work without modification.
 * - `primaryColor` is forwarded as `--icon-primary-color` and `color`,
 *   propagating to any `currentColor` fills inside the glyph.
 * - When `onClick` is provided, the wrapper is a `<button>` for keyboard
 *   accessibility (satisfies jsx-a11y/click-events-have-key-events).
 */

const sizeToPx: Record<IconSizes, string> = {
  [IconSizes.Small]: '16px',
  [IconSizes.Medium]: '24px',
  [IconSizes.Large]: '32px',
  [IconSizes.Xlarge]: '48px',
};

// Accept any component that can render — this intentionally doesn't require
// SVGProps so that zero-arg glyphs (e.g. `() => <svg>…</svg>`) work as-is,
// matching the old @atlaskit/icon contract.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GlyphComponent = ComponentType<any>;

type InternalIconProps = BitkitIconTypes & {
  glyph: GlyphComponent;
  secondaryColor?: string;
  testId?: string;
};

export const InternalIcon = ({
  glyph: Glyph,
  label,
  onClick,
  primaryColor,
  secondaryColor,
  size = IconSizes.Medium,
  testId,
}: InternalIconProps) => {
  const dimension = sizeToPx[size];

  const sharedStyle = {
    display: 'inline-block',
    width: dimension,
    height: dimension,
    flexShrink: 0,
    '--icon-primary-color': primaryColor ?? token('color.icon'),
    '--icon-secondary-color': secondaryColor,
    color: primaryColor ?? token('color.icon'),
  } as const;

  // Styles applied to the child SVG via a wrapper. Mirrors the old
  // @atlaskit/icon CSS: the SVG fills the container, uses
  // var(--icon-primary-color) for color, and var(--icon-secondary-color)
  // for fill (two-tone icons).
  const svgContainerStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    verticalAlign: 'bottom',
    pointerEvents: 'none' as const,
  };

  // When no label is provided, the icon is decorative — hide it from
  // assistive technology (matches the old @atlaskit/icon behaviour).
  const a11yProps = label
    ? { role: 'img' as const, 'aria-label': label }
    : { 'aria-hidden': true as const };

  // Use a <button> when onClick is provided so the element is interactive and
  // keyboard-accessible (satisfies jsx-a11y/click-events-have-key-events).
  if (onClick) {
    return (
      <button
        type="button"
        aria-label={label}
        onClick={onClick}
        data-testid={testId}
        style={{
          ...sharedStyle,
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
        }}
      >
        <span style={svgContainerStyle}>
          <Glyph role="presentation" />
        </span>
      </button>
    );
  }

  return (
    <span {...a11yProps} data-testid={testId} style={sharedStyle}>
      <span style={svgContainerStyle}>
        <Glyph role="presentation" />
      </span>
    </span>
  );
};
